import React, { useState, useEffect } from 'react';
import { FaNewspaper, FaCalendarAlt, FaChevronRight } from 'react-icons/fa';
import Footer from '../components/Footer';
import PageCover from '../components/PageCover';
import { useLanguage } from '../context/LanguageContext';
import { newsAPI } from '../services/api';

const NewsPage = () => {
  const { language } = useLanguage();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const res = await newsAPI.getAll();
        setNews(res.data || []);
      } catch (err) {
        console.error('Error fetching news:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchNews();
  }, []);

  const title = language === 'ar' ? 'الأخبار' : 'Actualités';
  const subtitle = language === 'ar' ? 'آخر أخبار الرابطة الوطنية لكرة القدم للهواة' : 'Dernières actualités de la Ligue Nationale de Football Amateur';

  return (
    <div style={styles.container}>
      <PageCover title={title} subtitle={subtitle} icon={FaNewspaper} />

      <section style={styles.content}>
        {loading ? (
          <div style={styles.loading}>
            <div style={styles.spinner}></div>
          </div>
        ) : news.length === 0 ? (
          <div style={styles.noData}>
            <FaNewspaper size={50} color="#ccc" />
            <p>{language === 'ar' ? 'لا توجد أخبار حالياً' : 'Aucune actualité pour le moment'}</p>
          </div>
        ) : (
          <div style={styles.newsGrid}>
            {news.map((item) => (
              <div key={item.id} style={styles.newsCard}>
                <div style={styles.imageWrapper}>
                  {item.image_url ? (
                    <img src={item.image_url} alt={item.title} style={styles.newsImage} />
                  ) : (
                    <div style={styles.newsImagePlaceholder}>
                      <FaNewspaper size={40} color="rgba(255,255,255,0.3)" />
                    </div>
                  )}
                  <div style={styles.dateBadge}>
                    <FaCalendarAlt style={{ marginRight: language === 'ar' ? '0' : '5px', marginLeft: language === 'ar' ? '5px' : '0' }} />
                    {new Date(item.published_at).toLocaleDateString(language === 'ar' ? 'ar-TN' : 'fr-FR')}
                  </div>
                </div>
                <div style={styles.newsContent}>
                  <h3 style={{ ...styles.newsTitle, textAlign: language === 'ar' ? 'right' : 'left', direction: language === 'ar' ? 'rtl' : 'ltr' }}>
                    {language === 'ar' ? (item.title_ar || item.title) : item.title}
                  </h3>
                  <p style={{ ...styles.newsExcerpt, textAlign: language === 'ar' ? 'right' : 'left', direction: language === 'ar' ? 'rtl' : 'ltr' }}>
                    {language === 'ar' ? (item.content_ar || item.content) : item.content}
                  </p>
                  <div style={{ ...styles.readMore, flexDirection: language === 'ar' ? 'row-reverse' : 'row' }}>
                    <span>{language === 'ar' ? 'اقرأ المزيد' : 'Lire la suite'}</span>
                    <FaChevronRight style={{ transform: language === 'ar' ? 'rotate(180deg)' : 'none' }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    background: '#f8fafc'
  },
  content: {
    maxWidth: '1200px',
    margin: '40px auto',
    padding: '0 20px',
    minHeight: '400px'
  },
  newsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
    gap: '30px'
  },
  newsCard: {
    background: 'white',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
    transition: 'transform 0.3s ease, boxShadow 0.3s ease',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column'
  },
  imageWrapper: {
    position: 'relative',
    height: '240px',
    overflow: 'hidden'
  },
  newsImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'transform 0.5s ease'
  },
  newsImagePlaceholder: {
    width: '100%',
    height: '100%',
    background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  dateBadge: {
    position: 'absolute',
    bottom: '15px',
    left: '15px',
    background: 'rgba(255,255,255,0.95)',
    padding: '6px 14px',
    borderRadius: '25px',
    fontSize: '13px',
    fontWeight: '600',
    color: '#1e293b',
    display: 'flex',
    alignItems: 'center',
    boxShadow: '0 4px 10px rgba(0,0,0,0.1)'
  },
  newsContent: {
    padding: '24px',
    flex: 1,
    display: 'flex',
    flexDirection: 'column'
  },
  newsTitle: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#1e293b',
    marginBottom: '12px',
    lineHeight: '1.4'
  },
  newsExcerpt: {
    fontSize: '15px',
    color: '#64748b',
    lineHeight: '1.6',
    marginBottom: '20px',
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden'
  },
  readMore: {
    marginTop: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    color: '#c41e3a',
    fontWeight: '700',
    fontSize: '14px'
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '100px 0'
  },
  spinner: {
    width: '50px',
    height: '50px',
    border: '4px solid #f3f3f3',
    borderTop: '4px solid #c41e3a',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  },
  noData: {
    textAlign: 'center',
    padding: '100px 0',
    color: '#94a3b8',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '15px'
  }
};

// Add keyframes for spinner
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.type = 'text/css';
  style.innerHTML = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    .news-card-hover:hover .news-image-zoom {
      transform: scale(1.1);
    }
  `;
  document.getElementsByTagName('head')[0].appendChild(style);
}

export default NewsPage;
